import { inject, Injector, StateKey, TransferState } from '@angular/core';
import { HttpRequest, HttpEvent, HttpHandlerFn, HttpResponse } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { PlatformService } from '@lentau/ui-kit/core/services/platform/platform.service';
import { NewUserWidgetsType1CacheKey } from '@lentau/api/shared/transfer-state.keys';
import { HttpStatus } from '@lentau/ui-kit/core/enums/http-status.enum';
import { IS_SEARCH_BOT } from '@lentau/ui-kit/core/tokens/is-search-bot';
import { withCatalogListingTransferStateKey } from '@lentau/api/shared/catalog-listing-transfer-state-key';
import {
  isCatalogItemsListingUrl,
  softStripCatalogListingTransferState,
} from '@lentau/api/shared/soft-strip-catalog-listing-transfer-state';

/**
 * Same-macrotask cache after TransferState.remove().
 * Avoids a second subscribe in the same tick going to the network.
 */
const browserTransferStateHitCache: Map<string, unknown> = new Map<string, unknown>();

export function transferStateInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const injector: Injector = inject(Injector);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return handleTransferStateInterceptor(req as HttpRequest<any>, next, injector);
}

export function handleTransferStateInterceptor(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  injector: Injector
): Observable<HttpEvent<unknown>> {
  const transferState: TransferState = injector.get(TransferState);
  const platformService: PlatformService = injector.get(PlatformService);
  const isSearchBot: boolean = injector.get(IS_SEARCH_BOT, false);
  const key: StateKey<string> = getKey(request, platformService);

  if (platformService.isBrowser) {
    // Master-like: HttpClient serves TransferState for all keys (including home widgets).
    return handleBrowserTransferStateRead(request, next, transferState, key);
  }

  if (isSearchBot) {
    // Bots do not hydrate — skip TransferState write.
    return next(request);
  }

  return handleServerTransferStateWrite(request, next, transferState, key);
}

const handleBrowserTransferStateRead: (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  transferState: TransferState,
  key: StateKey<string>
) => Observable<HttpEvent<unknown>> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  transferState: TransferState,
  key: StateKey<string>
): Observable<HttpEvent<unknown>> => {
  const cacheKey: string = key as string;

  const cached: unknown | undefined = browserTransferStateHitCache.get(cacheKey);
  if (cached !== undefined) {
    return ofOk(cached);
  }

  if (!transferState.hasKey(key)) {
    return next(request);
  }

  const data: unknown = transferState.get(key, null);
  transferState.remove(key);
  cacheBrowserHitForCurrentMacrotask(cacheKey, data);

  return ofOk(data);
};

const handleServerTransferStateWrite: (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  transferState: TransferState,
  key: StateKey<string>
) => Observable<HttpEvent<unknown>> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  transferState: TransferState,
  key: StateKey<string>
): Observable<HttpEvent<unknown>> =>
  next(request).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (!(event instanceof HttpResponse)) {
        return;
      }

      maybeStoreTransferState(request, event, transferState, key);
    })
  );

const maybeStoreTransferState: (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  response: HttpResponse<unknown>,
  transferState: TransferState,
  key: StateKey<string>
) => void = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  response: HttpResponse<unknown>,
  transferState: TransferState,
  key: StateKey<string>
): void => {
  if (response.status !== HttpStatus.success || response.url?.includes('/auth/')) {
    return;
  }

  // Allowlist short-circuit / empty SSR stubs — no TransferState bloat.
  if (response.body == null) {
    return;
  }

  const bodyForTs: unknown = prepareBodyForTransferState(response.url ?? request.url, response.body);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transferState.set(key, bodyForTs as any);
};

const prepareBodyForTransferState: (url: string, body: unknown) => unknown = (url: string, body: unknown): unknown => {
  if (!isCatalogItemsListingUrl(url)) {
    return body;
  }

  return softStripCatalogListingTransferState(body);
};

const cacheBrowserHitForCurrentMacrotask: (cacheKey: string, data: unknown) => void = (
  cacheKey: string,
  data: unknown
): void => {
  browserTransferStateHitCache.set(cacheKey, data);
  queueMicrotask((): void => {
    browserTransferStateHitCache.delete(cacheKey);
  });
};

const ofOk: (body: unknown) => Observable<HttpResponse<unknown>> = (body: unknown): Observable<HttpResponse<unknown>> =>
  of(new HttpResponse({ body, status: 200 }));

const getKey: (request: HttpRequest<unknown>, platformService: PlatformService) => StateKey<string> = (
  request: HttpRequest<unknown>,
  platformService: PlatformService
): StateKey<string> => {
  if (request.urlWithParams.includes(NewUserWidgetsType1CacheKey)) {
    return NewUserWidgetsType1CacheKey as StateKey<string>;
  }

  const pathname: string = clearTimeStamp(request.urlWithParams, platformService);

  return withCatalogListingTransferStateKey(pathname, request.body) as StateKey<string>;
};

const clearTimeStamp: (url: string, platformService: PlatformService) => string = (
  url: string,
  platformService: PlatformService
): string => {
  const urlObj: URL = new URL(url);
  urlObj.searchParams.delete('timestamp');

  let pathname: string = urlObj.pathname;

  if (platformService.isBrowser) {
    pathname = pathname.replace('/api-gateway/v1', '');
  } else {
    pathname = pathname.replace('/v1', '');
  }

  return pathname;
};
